/**
 * 토큰 갱신 로직
 *
 * Refresh Token을 사용하여 Access Token 갱신
 */

import { ENDPOINTS } from './config';
import { STORAGE_KEYS, saveTokens, getTokenData } from './storage';
import { classifyAuthError } from './errorUtils';

// 토큰 갱신 진행 중 플래그
let isRefreshing = false;
// 토큰 갱신 대기 중인 요청들
let pendingRequests = [];

/**
 * 토큰 갱신 진행 중 여부
 */
export function getIsRefreshing() {
  return isRefreshing;
}

export function setIsRefreshing(value) {
  isRefreshing = value;
}

/**
 * 대기 중인 요청 추가
 */
export function addPendingRequest(callback) {
  pendingRequests.push(callback);
}

/**
 * 대기 중인 요청들 처리
 */
export function resolvePendingRequests(token) {
  pendingRequests.forEach(callback => callback(token));
  pendingRequests = [];
}

/**
 * 토큰 갱신 함수
 * @returns {Promise<{success: boolean, token?: string, errorType?: string, error?: Error}>}
 */
export async function refreshAccessToken() {
  const { refreshToken, deviceId } = getTokenData();

  console.log('[refreshAccessToken] 토큰 갱신 시작:', {
    hasRefreshToken: !!refreshToken,
    deviceId,
    targetUrl: ENDPOINTS.graphql
  });

  if (!refreshToken || !deviceId) {
    console.error('[refreshAccessToken] 필수 데이터 누락');
    return {
      success: false,
      errorType: 'MISSING_DATA',
      error: new Error('Missing refresh token or device ID')
    };
  }

  try {
    const response = await fetch(ENDPOINTS.graphql, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          mutation RefreshToken($input: RefreshTokenInput!) {
            refreshToken(input: $input) {
              accessToken
              refreshToken
              expiresIn
              tokenType
              deviceId
              user {
                id
                userName
                authority
              }
            }
          }
        `,
        variables: {
          input: {
            refreshToken,
            deviceId
          }
        }
      })
    });

    // HTTP 상태 코드 확인
    if (response.status >= 500) {
      console.error('[refreshAccessToken] 서버 에러:', response.status);
      return {
        success: false,
        errorType: 'NETWORK_ERROR',
        error: new Error(`Server error: ${response.status}`)
      };
    }

    const result = await response.json();

    if (result.errors) {
      const firstError = result.errors[0];
      const errorType = classifyAuthError(firstError);
      console.error('[refreshAccessToken] 토큰 갱신 실패:', {
        errors: result.errors,
        classifiedType: errorType
      });
      return {
        success: false,
        errorType,
        error: new Error(firstError.message)
      };
    }

    const { accessToken, refreshToken: newRefreshToken, expiresIn } = result.data.refreshToken;

    // 새 토큰 저장
    saveTokens({ accessToken, refreshToken: newRefreshToken, expiresIn });

    console.log('[refreshAccessToken] 토큰 갱신 성공');
    return { success: true, token: accessToken };
  } catch (error) {
    const errorType = classifyAuthError(error);
    console.error('[refreshAccessToken] 토큰 갱신 에러:', { error, classifiedType: errorType });
    return {
      success: false,
      errorType,
      error
    };
  }
}
